import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '../supabase-client.ts';
import hybridAuthService from '../services/hybridAuthService.js';
import { getAllBookConfigs } from '../config/bookRegistry.js';
import Header from './Header';
import '../css/LoadingSpinner.css';
import '../css/ReadingList.css';
import songOfRolandCover from '../images/SongRoland.png';
import laysCover from '../images/Lais.png';
import donQuixoteCover from '../images/DonQuixote.png';
import divineComedyCover from '../images/DivineComedy.png';
import ifrsCover from '../images/IFRS_Accounting_book_cover_image.jpg';
import iliadCover from '../images/TheIliad.png';
import odysseyCover from '../images/Odyssey.png';
import antigoneCover from '../images/Antigone.png';
import oedipusCover from '../images/OedipusTheKing.png';

const coverImageMap = {
  'SongRoland.png': songOfRolandCover,
  'Lais.png': laysCover,
  'DonQuixote.png': donQuixoteCover,
  'DivineComedy.png': divineComedyCover,
  'IFRS_Accounting_book_cover_image.jpg': ifrsCover,
  'TheIliad.png': iliadCover,
  'Odyssey.png': odysseyCover,
  'Antigone.png': antigoneCover,
  'OedipusTheKing.png': oedipusCover,
};

// ReadingList component displays the user's reading list in a styled layout
function ReadingList({ onLogout, isRestrictedClass = false }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [booksState, setBooks] = useState([]);
  const [userInfo, setUserInfo] = useState({ email: '', cefr_level: '' });
  const restrictedClassId = 'ac800e1b-18a3-4dfd-9839-778e41489323';

  useEffect(() => {
    if (isRestrictedClass) {
      setLoading(false);
      return;
    }

    const fetchBooks = async () => {
      if (!isSupabaseConfigured || !supabase) {
        setBooks([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      // 1. Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setBooks([]);
        setLoading(false);
        return;
      }
      // Fetch user info for display
      const { data: userData, error: userError } = await supabase
        .from('USERS')
        .select('class_id, email, cefr_level')
        .eq('email', user.email)
        .single();
      if (userData) {
        setUserInfo({ email: userData.email, cefr_level: userData.cefr_level, class_id: userData.class_id });
      }
        if (userError || !userData) {
          // If query fails, at least populate email from authenticated user
          setUserInfo({ email: user.email, cefr_level: '', class_id: null });
          setBooks([]);
          setLoading(false);
          return;
        }
      const classId = userData.class_id;

      // If this class is restricted from reading list, render the fallback screen.
      if (classId === restrictedClassId) {
        setBooks([]);
        setLoading(false);
        return;
      }

      // 3. Get allowed book_ids from CLASS_BOOK_ACCESS
      const { data: accessData, error: accessError } = await supabase
        .from('CLASS_BOOK_ACCESS')
        .select('book_id')
        .eq('class_id', classId);

      if (accessError || !accessData) {
        setBooks([]);
        setLoading(false);
        return;
      }

      const bookIds = accessData.map(row => row.book_id);

      if (bookIds.length === 0) {
        setBooks([]);
        setLoading(false);
        return;
      }

      // 4. Get book details from BOOKS table
      const { data: booksData, error: booksError } = await supabase
        .from('BOOKS')
        .select('*')
        .in('book_id', bookIds);

      if (booksError || !booksData) {
        setBooks([]);
      } else {
        setBooks(booksData);
      }
      setLoading(false);
    };

    fetchBooks();
  }, [isRestrictedClass]);

  // Handle logout: clear user and redirect to login
  const handleLogout = async () => {
    await hybridAuthService.logout();
    onLogout();
    navigate('/');
  };

  // Handle clicking a book card to view chapters
  const handleBookClick = (book) => {
    const configs = getAllBookConfigs();
    const normalizeTitle = (value) => {
      if (!value) return '';
      return value.toLowerCase().replace(/^the\s+/, '').trim();
    };

    const normalizedTitle = normalizeTitle(book?.title);
    const matchingConfig = Object.values(configs).find(config =>
      (book.book_id && config.bookId === book.book_id) ||
      (book.title && config.displayName === book.title) ||
      (book.title && config.title === book.title) ||
      (normalizedTitle && normalizeTitle(config.displayName) === normalizedTitle)
    );

    if (matchingConfig) {
      navigate(`/${matchingConfig.route}`);
      return;
    }

  };

  const getBookConfig = (book) => Object.values(getAllBookConfigs()).find(config => (
    (book?.book_id && config.bookId === book.book_id) ||
    (book?.title && (config.displayName === book.title || config.title === book.title))
  ));

  if (isRestrictedClass || userInfo.class_id === restrictedClassId) {
    return (
      <div className="readinglist-root readinglist-restricted-root">
        <Header userInfo={userInfo} onLogout={handleLogout} />
        <div className="readinglist-content readinglist-restricted-content">
          <div className="readinglist-restricted-card">
            <div className="readinglist-restricted-eyebrow">Restricted Access</div>
            <h1 className="readinglist-restricted-title">Reading List is not available for this class.</h1>
            <p className="readinglist-restricted-copy">
              This class uses Dashboard for navigation. You can return there anytime to continue.
            </p>
            <div className="readinglist-restricted-actions">
              <button className="readinglist-dashboard-btn" type="button" onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </button>
              <button className="readinglist-secondary-btn" type="button" onClick={handleLogout}>
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <div className="loading-spinner-container"><div className="loading-spinner"></div></div>;
  
  if (!booksState.length) {
    return (
      <div className="readinglist-root">
        <Header userInfo={userInfo} onLogout={handleLogout} />
        <div className="readinglist-content">
          <div className="readinglist-no-books">
            <h1>No books available for your class.</h1>
            <p>Please contact your instructor if you believe this is an error.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="readinglist-root">
      <Header userInfo={userInfo} onLogout={handleLogout} />
      {/* Main Content */}
      <div className="readinglist-content">
        <h1 className="readinglist-main-title">Reading List</h1>
        <div className="readinglist-list">
          {booksState.map((book, idx) => {
            const config = getBookConfig(book);
            const cover = config?.coverImage ? coverImageMap[config.coverImage] : null;
            return (
            <article key={idx} className="readinglist-card">
              {cover ? <img className="readinglist-cover" src={cover} alt="" /> : <div className="readinglist-cover readinglist-cover-placeholder" aria-hidden="true" />}
              <div className="readinglist-book-info">
                <div className="readinglist-book-title">{book.title}</div>
                <div className="readinglist-book-author">{book.author || config?.author || 'ReadSphere collection'}</div>
              </div>
              <button className="readinglist-read-btn" onClick={() => handleBookClick(book)}>
                Read now
                <span className="readinglist-arrow" aria-hidden="true">{'>'}</span>
              </button>
            </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default ReadingList;
